import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../utils/LanguageContext';
import axios from 'axios';
import { Loader, Calendar, Folder } from 'lucide-react';
import getHelmetConfig from "../utils/helmetConfig";
import {Helmet} from "react-helmet-async";

const API_BASE_URL = 'https://texla.uz/api/v1';

// Skeleton Loader
const NewsSkeleton = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden animate-pulse">
        <div className="h-48 bg-gray-200"></div>
        <div className="p-4">
            <div className="h-4 bg-gray-200 mb-2"></div>
            <div className="h-6 bg-gray-200 mb-4"></div>
            <div className="h-4 bg-gray-200 mb-2"></div>
            <div className="h-4 bg-gray-200"></div>
        </div>
    </div>
);

const NewsPage = () => {
    const { t, language } = useLanguage();
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 9;

    useEffect(() => {
        fetchNews();
    }, [language]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentPage]);

    const fetchNews = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/news/`);
            if (response.data && Array.isArray(response.data)) {
                const formattedNews = response.data.map(item => {
                    const dateObj = new Date(item.created_at);
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const year = dateObj.getFullYear();
                    const formattedDate = `${day}.${month}.${year}`;

                    return {
                        id: item.id,
                        title: item[`title_${language}`] || item.title_uz || item.title,
                        text: item[`text_${language}`] || item.text_uz || item.text,
                        image: item.image || 'https://via.placeholder.com/400x250',
                        category: item.categories?.[0]?.[`title_${language}`] || item.categories?.[0]?.title_uz || item.categories?.[0]?.title || t('home.news'),
                        date: formattedDate,
                        created_at: item.created_at
                    };
                });
                setNews(formattedNews);
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent || "";
    };

    // Pagination
    const totalPages = Math.ceil(news.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedNews = news.slice(startIndex, startIndex + itemsPerPage);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <Loader className="animate-spin text-gray-400" size={48} />
            </div>
        );
    }

    const helmetConfig = getHelmetConfig(language);
    const pageConfig = helmetConfig.blog;

    return (
        <div className="bg-white">
            <Helmet>
                <title>{pageConfig.title}</title>
                <meta name="description" content={pageConfig.description} />
                <meta name="keywords" content={pageConfig.keywords} />
            </Helmet>
            {/* Breadcrumb */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-8">
                    <Link to={"/"} className="hover:text-gray-900">{t('breadcrumb.home')}</Link>
                    <span>/</span>
                    <span className="text-gray-900">{t('home.news')}</span>
                </div>
            </div>

            {/* Header */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-2">{t('home.news')}</h1>
                <p className="text-gray-600 text-lg">{news.length} {t('news.itemsFound')}</p>
            </section>

            {/* News Grid */}
            {news.length > 0 ? (
                <>
                    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {paginatedNews.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/blog/${item.id}`}
                                    className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition group"
                                >
                                    <div className="relative h-48 bg-gray-200 overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                                        />
                                        <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                                            <Folder size={16} />
                                            {item.category}
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                            <Calendar size={16} />
                                            {item.date}
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-orange-600 transition">
                                            {item.title}
                                        </h3>
                                        <p className="text-gray-600 text-sm line-clamp-3">
                                            {stripHtml(item.text).substring(0, 150)}...
                                        </p>
                                        <div className="mt-4 text-orange-600 font-semibold group-hover:text-blue-700">
                                            {t('news.readMore')} →
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                            <div className="flex justify-center items-center gap-2">
                                {currentPage > 1 && (
                                    <button
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 transition"
                                    >
                                        {t('shop.previous')}
                                    </button>
                                )}

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`px-4 py-2 rounded-lg transition ${
                                            currentPage === page
                                                ? 'bg-orange-600 text-white'
                                                : 'border border-gray-300 text-gray-900 hover:bg-gray-50'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                {currentPage < totalPages && (
                                    <button
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-900 hover:bg-gray-50 transition"
                                    >
                                        {t('shop.next')}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}
                </>
            ) : (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
                    <p className="text-gray-600 text-lg">{t('shop.noProductsFound')}</p>
                </section>
            )}
        </div>
    );
};

export default NewsPage;